import { useEffect, useState } from "react";

/**
 * What's new, release by release (2026-10-11). Shown on the What's new page
 * and in the sidebar dot. Written for pilots, not developers: say what a pilot
 * sees or does differently, leave out how.
 *
 * How to keep it:
 *   - Add what you change to the top entry while it has `date: null`. An
 *     entry without a date is the next release and is not shown.
 *   - When releasing, give it the release date. release-desktop.sh refuses to
 *     publish a version that has no dated entry here, and changelog.test.js
 *     checks desktop/package.json's version is listed.
 *   - Changes to the web app reach pilots in a browser straight away but reach
 *     the desktop app with its next release; list them under that release.
 *
 * kind: "new" | "improved" | "fixed"
 */
export const RELEASES = [
  {
    version: "0.9.19",
    date: "2026-10-23",
    items: [
      ["fixed", "IN on the Fenix A320 (and any aircraft whose parking brake never reads as set): if VACARS5 hasn't once seen your brake set during the flight, IN is the engines off and 30 seconds stopped. It used to never come, and you had to file by hand."],
      ["fixed", "Live Tracking's map no longer snaps back to your aircraft every few seconds. It follows smoothly until you drag it to look around; Re-centre brings it back."],
      ["fixed", "Debrief maps and the Live Tracking route no longer draw a line from wherever the sim had the aircraft while loading. Cards already saved are redrawn."],
      ["fixed", "Closing the VACARS5 window left it running in the tray with no way back: the tray's Open VACARS5, and opening VACARS5 again, did nothing, and update checks failed until a restart. Both now open a new window."],
      ["improved", "The app log notes the parking brake, engines and door as your sim reports them, once and then on each change, so a missing OUT or IN can be traced to the signal behind it."],
    ],
  },
  {
    version: "0.9.18",
    date: "2026-10-19",
    items: [
      ["fixed", "Cabin announcements play with the sim in front. With VACARS5 in the background, the computer paused each clip to save power before it had loaded, so announcements went silent. This is why some testers heard none after takeoff."],
      ["improved", "TAXI OUT now starts as soon as the push starts: parking brake off and the aircraft moving, engines on or not, with the doors closed if your aircraft reports them. That's how Air Canada, Rouge and Jazz aircraft record OUT. Since 0.9.17 it waited for an engine to be running."],
      ["new", "VACARS5 tells you when your position reports aren't reaching FlyVAC: a notice in the sidebar and on Live Tracking, with how many are waiting on your computer. Nothing is lost either way; now you can see it."],
      ["new", "The overlay's hotkeys can be changed in Settings. If another app already uses one, Settings says so in red, so you know to pick another."],
      ["improved", "The Live Map moves aircraft smoothly between position updates instead of jumping about 2 nm every 15 seconds, and Follow pans with the aircraft instead of zooming out and back in each time."],
      ["improved", "The Live Map shows each flight's phase as its pilot's VACARS5 shows it. It used to guess from one snapshot, so a level-off in the climb read CRUISE there."],
      ["new", "Your debriefs on myVAC: every flight with one has a picture icon in your myVAC logbook that opens the card. The card in VACARS5 has an Open in myVAC button."],
      ["fixed", "Beta test checklist, item 4.1: checks TAXI OUT as the push starts."],
    ],
  },
  {
    version: "0.9.17",
    date: "2026-10-18",
    items: [
      ["improved", "TAXI OUT starts the way crews call OUT: an engine running and the parking brake off. A push and start used to read BOARDING until you taxied forward faster than a push."],
      ["improved", "IN, at the gate, now wants the door open too, as well as the parking brake set and the engines off. A delay with the engines shut down short of the gate no longer ends the flight there. Only for aircraft that report their door: if VACARS5 never saw yours open at the departure gate, brake and engines alone still do it."],
      ["fixed", "Altitude in MSFS add-ons with their own altimeter (the FSS E175, for one): below 18,000 ft it shows your local altitude, not standard pressure."],
      ["fixed", "A telemetry error when a flight was cancelled while VACARS5 was sending its position."],
      ["fixed", "A flight's first positions could be refused by the server while MSFS was still loading it (it sends nonsense altitudes then), and nothing was recorded until you started again. Impossible readings are now ignored."],
      ["fixed", "The approach announcement could play at takeoff, the moment the wheels left the runway, and then the in-flight and descent announcements never played. Takeoff is now the climb from the first second."],
      ["fixed", "Restarting VACARS5 mid-flight (or letting it update) no longer replays the announcements you've already heard, and carries on with the ones still to come."],
      ["improved", "The app log notes every phase change and every announcement: played, at what volume, or why not. A beta report with a missing announcement now shows what happened."],
      ["fixed", "Beta test checklist: item 5.3 checks the in-flight announcement about 90 seconds after takeoff, where it has played since 0.9.12 (it said at the cruise level), and items 4.1 and 8.2 follow the new TAXI OUT and IN rules."],
    ],
  },
  {
    version: "0.9.16",
    date: "2026-10-18",
    items: [
      ["new", "Flight debriefs. When your flight files, Live Tracking shows a debrief card: route, block time, landing rate and how it ranks against your own, fuel burned against the plan, distance flown, and your climb, cruise and descent. The Logbook has an icon on each flight with one."],
      ["new", "Real FAA delays and ground stops: the Flight Plan page shows any FAA traffic program (ground stop, ground delay program, reroute) at your departure or arrival airport, before and during the flight, as Live Tracking already did. US airports only."],
      ["fixed", "The FAA advisories card is readable in the light theme."],
      ["fixed", "Live Tracking in day mode: the departure and arrival codes on the map are readable, as on the Live Map since 0.9.13."],
      ["new", "Admins: beta reports have New, Reviewed and Archived tabs. Mark one reviewed and the next new one opens; archive the reviewed ones in one click; leave a note for the other admins. Nothing is deleted."],
      ["fixed", "Only one copy of VACARS5 runs at a time. Opening it again brings the running window to the front. Two copies used to log the same flight twice."],
      ["fixed", "A flight is filed once, even if two copies of VACARS5 (on one computer or two) send it at the same moment."],
      ["fixed", "Beta test: a sent report clears its marks. They used to come back when you returned to the page."],
      ["new", "Share your debrief: copy its link and paste it in Discord, where it shows as a picture, or save it as an image. Turn sharing off in Settings if you'd rather not."],
      ["improved", "The debrief compares your fuel with the plan you opened on the Flight Plan page. Made it on SimBrief or in myVAC's dispatcher instead? Your latest SimBrief plan is used, when it is for the same route and from the same day."],
      ["fixed", "Beta test checklist, item 7.3: the approach announcement is checked at 10,000 ft, where it has played since 0.9.13. It said 5,000 ft."],
    ],
  },
  {
    version: "0.9.15",
    date: "2026-10-17",
    items: [
      ["new", "Remember me, on the sign-in page: stay signed in for 30 days, renewed while VACARS5 is open, so you rarely have to sign in again. Ticked for you in the desktop app; unticked in a browser, where closing the tab signs you out."],
      ["improved", "Changing your password signs you out everywhere you were signed in."],
      ["fixed", "Init Flight shows the airline that really flies your bid: Express for a Jazz E175 or Dash 8, Leisure for Rouge, PAL Airlines for PAL. Every bid used to load as Mainline, which also picked the mainline sound pack and looked gates up under the wrong airline."],
      ["new", "VATSIM ID in Settings. The Live Map shows when you're connected to VATSIM, but only if your record has your VATSIM ID, and until now it could only be entered when you first joined. Flights without one show NO ID."],
    ],
  },
  {
    version: "0.9.14",
    date: "2026-10-16",
    items: [
      ["fixed", "Dashboard bids and the Logbook: every column is the same size and colour. Type, arrival time and FIN · REG used to be smaller and grey, as did the logbook's type, registration and date."],
    ],
  },
  {
    version: "0.9.13",
    date: "2026-10-16",
    items: [
      ["new", "Discord status works: with Discord open, your profile shows your flight, route, aircraft, altitude and time since pushback. It never connected before; VACARS5 was set up with a Discord application that did not exist."],
      ["fixed", "The approach announcement (cabin secure) plays descending through 10,000 ft, where crews make it. It used to wait until 5,000 ft."],
      ["fixed", "Live Map in day mode: the departure and arrival codes are readable. Their dark box was narrower than the code, which ran out past it in light grey."],
    ],
  },
  {
    version: "0.9.12",
    date: "2026-10-16",
    items: [
      ["new", "Text size, in Settings: make everything in the desktop app bigger, from 100% up to 140%. Kept per computer. In a browser, use its zoom (Ctrl + or Cmd +)."],
      ["fixed", "Logbook: flights and duty days are in the same size text as the rest of the page. They used to be a size smaller."],
      ["fixed", "The in-flight announcement plays a minute and a half after takeoff, as cabin crew make it. It used to wait for cruise, which could be ten minutes after levelling off."],
      ["fixed", "Levelling off in the descent (11,000 ft on an arrival, say) stays the descent. The moment of climb as the autopilot captures the altitude used to read as a climb, and stayed one."],
      ["fixed", "Overlay: NM flown is the distance you have actually flown. It used to stay at 0 after a departure that turns away from the destination first."],
      ["fixed", "The Embraer 175 is E75S everywhere, to match myVAC. Bids, the flight plan and the Live Map all know it by that code."],
      ["fixed", "Dashboard: the Rank tile uses the same lettering and size as Total time, Hub and Assignments. It used to be smaller and in a different font."],
      ["fixed", "Flight Plan: the boxes use one text size, labels use one font everywhere (Runway, Elevation and Trans used to look like data), and all buttons match."],
    ],
  },
  {
    version: "0.9.11",
    date: "2026-10-15",
    items: [
      ["fixed", "Altitude now matches your altimeter. VACARS5 used to show the aircraft's true height above sea level, which can differ by several hundred feet at cruise (33,300 ft shown while the altimeter read 32,600 on standard)."],
      ["fixed", "The cruise announcement and cruise detection compare the altimeter with your planned flight level, so they no longer wait on a warm or cold day."],
      ["new", "Admins: open or close beta sign-up from the Beta applications page, with an optional message for visitors. Closing keeps testers and past applications."],
      ["fixed", "A step down for traffic in the middle of the flight stays cruise. It used to count as the descent for the rest of the flight and play the descent announcement early."],
    ],
  },
  {
    version: "0.9.10",
    date: "2026-10-11",
    items: [
      ["new", "Beta sign-up: pilots apply at acars.flyvac.org/apply, and the sign-in page links there when the closed beta turns someone away."],
      ["new", "Admins: a Beta applications page to approve or decline applications and manage the tester list."],
    ],
  },
  {
    version: "0.9.9",
    date: "2026-10-11",
    items: [
      ["new", "What's new: this page. A dot in the sidebar marks a release you haven't read about yet."],
      ["new", "Pilot guide: one click from the sidebar and the sign-in page opens the latest guide (PDF)."],
      ["improved", "Live Map shows each flight's actual phase (pushback, climb, cruise, descent, approach and so on) instead of only boarding, taxi or in flight, and you can filter by phase."],
      ["fixed", "The sign-in page shows the version you're running instead of \"FlyVAC 5.0\"."],
    ],
  },
  {
    version: "0.9.8",
    date: "2026-10-08",
    items: [
      ["improved", "VACARS5 now lives at acars.flyvac.org. Nothing to do: the update installs itself and signs you in as before."],
      ["improved", "The download page is simply myvac.flyvac.org/downloads/vacars5/, no version number needed."],
    ],
  },
  {
    version: "0.9.7",
    date: "2026-10-07",
    items: [
      ["fixed", "Landing rate is the rate at touchdown, not the steepest point of the approach. The overlay, Live Tracking and your PIREP all show the same number."],
      ["fixed", "The cruise announcement waits for your planned cruise level, not the first ATC level-off on the climb."],
      ["fixed", "The arrival announcement waits until you've slowed to taxi speed, not the landing roll."],
      ["fixed", "Telemetry keeps flowing while MSFS is still loading, and VACARS5 recovers from a SimConnect data error by itself instead of needing Reconnect."],
      ["fixed", "Takeoff is no longer logged as a go-around."],
      ["fixed", "Discord status reconnects by itself after Discord is restarted."],
      ["fixed", "Aircraft names from some add-ons (Fenix and others) show cleanly instead of as internal codes."],
    ],
  },
  {
    version: "0.9.6",
    date: "2026-10-06",
    items: [
      ["new", "The VAC 2026 look, matching the new myVAC and the dispatcher dashboard, in light and dark."],
      ["improved", "The Beta test page reminds testers to bid on the new myVAC at myvac.flyvac.org, not vacanada.org."],
    ],
  },
  {
    version: "0.9.5",
    date: "2026-10-05",
    items: [
      ["new", "Beta test page: a checklist to work through on a test flight, sent to the team with one click."],
    ],
  },
  {
    version: "0.9.4",
    date: "2026-10-05",
    items: [
      ["new", "Your flown route is saved with the flight, so the map on your myVAC flight record shows it, as with VACARS3."],
      ["fixed", "Live Tracking's event log and route stay put when you visit another page and come back."],
      ["fixed", "Pushback is no longer mistaken for a climb."],
    ],
  },
  {
    version: "0.9.3",
    date: "2026-10-04",
    items: [
      ["new", "Flight Plan shows your assigned passenger load beside SimBrief's."],
      ["improved", "Dashboard bids show departure and arrival times, marked as local time."],
      ["fixed", "Cabin audio keeps playing when you change page."],
      ["fixed", "The aircraft from your bid is filed with the flight, and Rouge bids find their aircraft on Init Flight."],
    ],
  },
  {
    version: "0.9.2",
    date: "2026-10-04",
    items: [
      ["fixed", "Mac copies can find updates again. On Windows, VACARS5 asks before restarting to install one."],
      ["fixed", "Levelling off during the descent no longer ends the descent phase."],
    ],
  },
  {
    version: "0.9.1",
    date: "2026-10-04",
    items: [
      ["improved", "IN time is when the parking brake is set and the engines are off, as in VACARS3."],
      ["improved", "Bids you place after opening VACARS5 show up without restarting it."],
      ["fixed", "Several fixes from the first full MSFS test flight."],
    ],
  },
  {
    version: "0.9.0",
    date: "2026-10-03",
    items: [
      ["new", "First beta release. Updates download from myvac.flyvac.org by themselves on Windows; on a Mac, VACARS5 tells you when there's a new version to download."],
      ["new", "The version you're running is shown at the bottom of the sidebar."],
    ],
  },
];

/** Released entries only (those with a date), newest first. */
export const PUBLISHED = RELEASES.filter((r) => r.date);

export const LATEST = PUBLISHED[0]?.version || null;

/**
 * "0.9.10" > "0.9.9". Plain string comparison gets that wrong, and the dot in
 * the sidebar would stop appearing at the tenth patch release.
 */
export function compareVersions(a, b) {
  const pa = String(a).split(".").map(Number);
  const pb = String(b).split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
}

const SEEN_KEY = "vacars5_whats_new_seen";
const SEEN_EVENT = "vacars5:whats-new-seen";

/** The newest version this browser/install has opened the What's new page for. */
export function seenVersion() {
  try { return localStorage.getItem(SEEN_KEY); } catch { return null; }
}

export function markSeen(version = LATEST) {
  try { if (version) localStorage.setItem(SEEN_KEY, version); } catch { /* storage off: dot stays */ }
  window.dispatchEvent(new Event(SEEN_EVENT));
}

/** Is there a release the pilot hasn't looked at? */
export function hasUnseen(seen = seenVersion()) {
  return !!LATEST && (!seen || compareVersions(LATEST, seen) > 0);
}

/** For the sidebar dot: true until the What's new page is opened. */
export function useUnseenRelease() {
  const [unseen, setUnseen] = useState(() => hasUnseen());
  useEffect(() => {
    const update = () => setUnseen(hasUnseen());
    window.addEventListener(SEEN_EVENT, update);
    // A child's effects run before its parent's, so landing straight on the
    // page marks it seen before this listener exists. Look again now.
    update();
    return () => window.removeEventListener(SEEN_EVENT, update);
  }, []);
  return unseen;
}
